import { Suspense, lazy } from 'react';

import { Info } from 'lucide-react';
import { useParams } from 'react-router';

import { getBoxPlotData } from '../../lib/chart';
import { formatScore } from '../../lib/score';
import { useGetAssignment, useGetAssignmentStatistics } from '../../query/assignment';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const BoxPlotChart = lazy(() => import('../charts/box-plot-chart'));
const HistogramChart = lazy(() => import('../charts/histogram-chart'));

const AssignmentStatistics = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { data: assignment } = useGetAssignment(assignmentId!);
  const { data: assignmentStatistics } = useGetAssignmentStatistics(assignmentId!);

  if (!assignment) return <></>;
  if (!assignment.data) return <></>;
  if (!assignmentStatistics) return <></>;
  if (!assignmentStatistics.data) return <></>;

  // 과제 전체 점수
  const totalScore = assignment.data.problems.reduce((prev, curr) => {
    return prev + curr.score;
  }, 0);
  // 과제 중 최고 배점
  const maxScore = assignment.data.problems.reduce((prev, curr) => {
    return Math.max(prev, curr.score);
  }, 0);

  const { assignmentBoxPlot, problemBoxPlots, assignmentHistogram, problemHistograms, myScores } =
    assignmentStatistics.data;

  return (
    <div className="flex justify-center flex-col items-center gap-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 w-full">
        <div className="flex items-center gap-2 text-blue-800">
          <Info className="h-4 w-4" />
          <span className="text-sm font-medium">
            통계 정보: 미제출 학생은 모든 통계에서 제외되었습니다
          </span>
        </div>
      </div>

      <Tabs defaultValue="boxplot" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="boxplot">박스플롯</TabsTrigger>
          <TabsTrigger value="histogram">히스토그램</TabsTrigger>
        </TabsList>

        <TabsContent value="boxplot" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>점수 분포 박스플롯</CardTitle>
              <CardDescription>점수의 사분위수와 이상치를 시각화합니다</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 items-center w-full">
              <Card className="w-full" showTopBar={false}>
                <CardHeader>
                  <CardTitle>문제별 통계 테이블</CardTitle>
                  <CardDescription>각 문제별 상세 통계값을 테이블로 확인하세요</CardDescription>
                </CardHeader>
                <CardContent className="w-full">
                  <Table className="w-full">
                    <TableHeader>
                      <TableRow>
                        <TableHead>문제</TableHead>
                        <TableHead className="text-right">내 점수</TableHead>
                        <TableHead className="text-right">인원</TableHead>
                        <TableHead className="text-right">최솟값</TableHead>
                        <TableHead className="text-right">최댓값</TableHead>
                        <TableHead className="text-right">평균</TableHead>
                        <TableHead className="text-right">중앙값</TableHead>
                        <TableHead className="text-right">분산</TableHead>
                        <TableHead className="text-right">표준편차</TableHead>
                        <TableHead className="text-right">25%</TableHead>
                        <TableHead className="text-right">75%</TableHead>
                        <TableHead className="text-right">IQR</TableHead>
                      </TableRow>
                    </TableHeader>
                    {assignmentBoxPlot ? (
                      <TableBody>
                        <TableRow className="bg-muted/50">
                          <TableCell className="font-semibold">{'전체문제'}</TableCell>
                          <TableCell className="text-right">
                            {formatScore(
                              myScores.reduce((prev, curr) => {
                                return prev + curr.score;
                              }, 0),
                            )}
                          </TableCell>
                          <TableCell className="text-right">{assignmentBoxPlot.count}</TableCell>
                          <TableCell className="text-right">{assignmentBoxPlot.min}</TableCell>
                          <TableCell className="text-right">{assignmentBoxPlot.max}</TableCell>
                          <TableCell className="text-right">{assignmentBoxPlot.mean}</TableCell>
                          <TableCell className="text-right">{assignmentBoxPlot.median}</TableCell>
                          <TableCell className="text-right">{assignmentBoxPlot.variance}</TableCell>
                          <TableCell className="text-right">{assignmentBoxPlot.stdDev}</TableCell>
                          <TableCell className="text-right">
                            {assignmentBoxPlot.percentile_25}
                          </TableCell>
                          <TableCell className="text-right">
                            {assignmentBoxPlot.percentile_75}
                          </TableCell>
                          <TableCell className="text-right">{assignmentBoxPlot.IQR}</TableCell>
                        </TableRow>

                        {/* 각 문제별 행 */}
                        {problemBoxPlots.map((item, index) => (
                          <TableRow key={item.problemId}>
                            <TableCell className="font-medium">문제 {index + 1}</TableCell>
                            <TableCell className="text-right">
                              {formatScore(
                                myScores.find(myScore => {
                                  return item.problemId === myScore.problemId;
                                })?.score,
                              )}
                            </TableCell>
                            <TableCell className="text-right">{item.count}</TableCell>
                            <TableCell className="text-right">{item.min}</TableCell>
                            <TableCell className="text-right">{item.max}</TableCell>
                            <TableCell className="text-right">{item.mean}</TableCell>
                            <TableCell className="text-right">{item.median}</TableCell>
                            <TableCell className="text-right">{item.variance}</TableCell>
                            <TableCell className="text-right">{item.stdDev}</TableCell>
                            <TableCell className="text-right">{item.percentile_25}</TableCell>
                            <TableCell className="text-right">{item.percentile_75}</TableCell>
                            <TableCell className="text-right">{item.IQR}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    ) : (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={11} className="text-center py-2">
                            데이터가 없습니다.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </CardContent>
              </Card>

              <Suspense fallback={<div className="w-full h-64 flex items-center justify-center">Loading chart...</div>}>
                <BoxPlotChart
                  title={'전체 과제 점수 분포'}
                  minY={0}
                  maxY={totalScore}
                  data={{
                    data: assignmentBoxPlot
                      ? [
                          {
                            x: '전체과제',
                            y: getBoxPlotData(assignmentBoxPlot),
                          },
                        ]
                      : [{ x: '전체과제', y: [] }],
                  }}
                />
              </Suspense>

              <Suspense fallback={<div className="w-full h-64 flex items-center justify-center">Loading chart...</div>}>
                <BoxPlotChart
                  title={'문제별 점수 분포'}
                  minY={0}
                  maxY={maxScore}
                  data={{
                    data: problemBoxPlots.map((problemBoxPlot, index) => {
                      return {
                        x: `문제 ${index + 1}`,
                        y: getBoxPlotData(problemBoxPlot),
                      };
                    }),
                  }}
                />
              </Suspense>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="histogram" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>점수 분포 히스토그램</CardTitle>
              <CardDescription>점수 구간별 학생 수를 보여줍니다</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 items-center w-full">
              <Suspense fallback={<div className="w-full h-64 flex items-center justify-center">Loading chart...</div>}>
                <HistogramChart
                  title={'전체 과제 점수 분포'}
                  description={'전체 과제 점수 분포'}
                  bins={assignmentHistogram}
                />
              </Suspense>

              {problemHistograms.map((problemHistogram, index) => {
                return (
                  <Suspense key={problemHistogram.problemId} fallback={<div className="w-full h-64 flex items-center justify-center">Loading chart...</div>}>
                    <HistogramChart
                      title={`문제${index + 1} 점수 분포`}
                      description={`문제 ${index + 1} 점수 분포`}
                      bins={problemHistogram.bins}
                    />
                  </Suspense>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AssignmentStatistics;
